/**
 * Reading a CSV or Excel file for upload into a defined dataset: the first
 * sheet's header row is matched to the dataset's columns, and each row
 * becomes an object with values in the column's type. Rows keep their
 * spreadsheet line number, so a server error on "Row 12" can name the line.
 */
import { toIdentifier } from '../components/data-sources/dataSourceFormat';

const pad = (n) => String(n).padStart(2, '0');

/** A cell date as written in the sheet (no timezone shift): YYYY-MM-DD, with the time when it has one. */
export function dateText(d) {
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const hasTime = d.getHours() || d.getMinutes() || d.getSeconds();
  return hasTime ? `${date}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` : date;
}

/** Parse the first sheet of a CSV or Excel file into headers and rows (raw values and text as shown). */
export async function readSheet(arrayBuffer) {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return { headers: [], rows: [] };
  const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null, blankrows: true });
  const text = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '', blankrows: true });
  const headers = (raw[0] || []).map((h) => (h === null ? '' : String(h).trim()));
  const rows = [];
  for (let i = 1; i < raw.length; i++) {
    const values = raw[i] || [];
    if (values.every((v) => v === null || v === '')) continue;
    rows.push({ line: i + 1, values, text: text[i] || [] });
  }
  return { headers, rows };
}

/**
 * Match headers to columns by name, ignoring case, spaces and punctuation
 * ("Sales Owner" matches sales_owner).
 */
export function mapHeaders(headers, columns) {
  const byName = new Map(headers.map((h, index) => [toIdentifier(h), index]));
  const matched = [];
  const missing = [];
  for (const col of columns) {
    if (byName.has(col.name)) matched.push({ column: col, index: byName.get(col.name) });
    else missing.push(col);
  }
  const used = new Set(matched.map((m) => m.index));
  return {
    matched,
    missingRequired: missing.filter((c) => c.required),
    missingOptional: missing.filter((c) => !c.required),
    ignored: headers.filter((h, i) => h && !used.has(i)),
  };
}

/** One cell in its column's type; the server checks it again and names any value it cannot use. */
export function cellValue(raw, text, type) {
  if (raw === null || raw === undefined || raw === '') return null;
  switch (type) {
    case 'number':
      if (typeof raw === 'number') return raw;
      return String(raw).trim().replace(/,(?=\d{3}\b)/g, '');
    case 'boolean':
      return typeof raw === 'boolean' ? raw : String(raw).trim();
    case 'date':
      return raw instanceof Date ? dateText(raw) : String(raw).trim();
    default:
      // Text as shown in the sheet, so codes such as 00123 keep their leading zeros.
      return text !== undefined && text !== null && text !== '' ? String(text) : String(raw);
  }
}

/** Rows as objects keyed by the matched columns. */
export function buildRows(sheet, mapping) {
  return sheet.rows.map((row) => {
    const out = {};
    for (const { column, index } of mapping.matched) out[column.name] = cellValue(row.values[index], row.text[index], column.type);
    return out;
  });
}

/** Turn the server's "Row 12: ..." (index within the upload) into the spreadsheet line. */
export function translateRowError(message, sheet) {
  return String(message || '').replace(/^Row (\d+):/, (m, n) => {
    const row = sheet.rows[Number(n)];
    return row ? `Spreadsheet row ${row.line}:` : m;
  });
}

const NUMBER_TEXT = /^[-+]?(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/;
const BOOLEAN_TEXT = new Set(['true', 'false', 'yes', 'no']);
const DATE_TEXT = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?/;

/** The type a column's sample values suggest: the narrowest type every non-empty value fits. */
export function inferType(values) {
  const present = values.filter((v) => v !== null && v !== undefined && v !== '');
  if (present.length === 0) return 'string';
  const all = (test) => present.every(test);
  if (all((v) => v instanceof Date || (typeof v === 'string' && DATE_TEXT.test(v.trim())))) return 'date';
  if (all((v) => typeof v === 'boolean' || (typeof v === 'string' && BOOLEAN_TEXT.has(v.trim().toLowerCase())))) return 'boolean';
  if (all((v) => typeof v === 'number' || (typeof v === 'string' && NUMBER_TEXT.test(v.trim())))) {
    // Codes with leading zeros (00123) are text, not numbers.
    if (present.some((v) => typeof v === 'string' && /^0\d/.test(v.trim()))) return 'string';
    return 'number';
  }
  return 'string';
}

/**
 * Suggested columns for a new dataset from a sheet: one per header, named
 * from the header, typed from up to 200 sample rows, required when every
 * sampled row has a value.
 */
export function suggestColumns(sheet) {
  const sample = sheet.rows.slice(0, 200);
  const seen = new Set();
  const columns = [];
  sheet.headers.forEach((header, index) => {
    let name = toIdentifier(header);
    if (!name) return;
    while (seen.has(name)) name = `${name}_2`;
    seen.add(name);
    // A parser reads 00123 as the number 123; the text as shown keeps the leading zero for inference.
    const values = sample.map((r) => {
      const value = r.values[index];
      const text = r.text?.[index];
      return typeof value === 'number' && typeof text === 'string' && /^0\d/.test(text.trim()) ? text : value;
    });
    const type = inferType(values);
    columns.push({
      name,
      type,
      required: sample.length > 0 && values.every((v) => v !== null && v !== undefined && v !== ''),
      description: header !== name ? header : '',
    });
  });
  return columns;
}

/** A dataset title from a file name: "Sales targets 2026.xlsx" → "Sales targets 2026". */
export function titleFromFileName(fileName) {
  return String(fileName || '').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
}
